import { SimulationKernel, type KernelSnapshot, type PhaseHandler } from "../../core/lifecycle/kernel.ts";
import { EMPTY_HELD_INPUT } from "../../core/lifecycle/events.ts";
import {
  OWNER_KIND,
  commitCreate,
  commitTransfer,
  createLedgerState,
  commitAttendedTendBatch,
  commitEggSaleBatch,
  type LedgerState,
  type OwnerRef,
} from "../transactions/index.ts";
import { MANUAL_CHAIN_POINTS, MANUAL_CHAIN_TUNING } from "./config.ts";
import {
  appendManualChainEvent,
  cargoKind,
  countOwnedItems,
  createInitialManualChainState,
  deriveObjective,
  hashManualChainState,
} from "./state.ts";
import { resolveFirstYardMovement } from "./navigation.ts";
import type {
  ManualChainActiveAction,
  ManualChainInteraction,
  ManualChainRouteTarget,
  ManualChainS04Port,
  ManualChainSnapshot,
  ManualChainState,
  ManualChainTickInput,
} from "./types.ts";

const OWNER_ID = Object.freeze({ cargo: 1, pen: 2, market: 3 } as const);

function ref(state: LedgerState, ownerKind: number, ownerId: number): OwnerRef {
  const match = state.owners.find((candidate) => candidate.ownerKind === ownerKind && candidate.ownerId === ownerId);
  if (match === undefined) throw new Error("manual-chain owner missing");
  return Object.freeze({ ownerKind: match.ownerKind, ownerId: match.ownerId, version: match.version });
}

function replaceActorAction(state: LedgerState, actionId: number, present: boolean): LedgerState {
  return createLedgerState({
    items: state.items,
    owners: state.owners,
    actors: state.actors.map((actor) => actor.actorId === 1 ? { ...actor, actionId, present, enabled: present } : actor),
    receipts: state.receipts,
    events: state.events,
    currency: state.currency,
    navRevision: state.navRevision,
    nextEventSequence: state.nextEventSequence,
  });
}

function inRange(state: Readonly<ManualChainState>, target: "field" | "pen" | "market"): boolean {
  const point = MANUAL_CHAIN_POINTS[target];
  const dx = state.player.xMicroWU - point.xMicroWU;
  const dz = state.player.zMicroWU - point.zMicroWU;
  return dx * dx + dz * dz <= 1_000_000 * 1_000_000;
}

function eligibleQuantity(state: Readonly<ManualChainState>, verb: ManualChainInteraction): number {
  const cargoCount = countOwnedItems(state, OWNER_KIND.ACTOR_CARGO, OWNER_ID.cargo);
  const stagedCount = countOwnedItems(state, OWNER_KIND.PEN_STAGING, OWNER_ID.pen, 1);
  const eggCount = countOwnedItems(state, OWNER_KIND.PEN_OUTPUT, OWNER_ID.pen, 2);
  switch (verb) {
    case "harvest": return inRange(state, "field") && cargoKind(state) !== "egg" && cargoCount < state.player.cargoCapacity && state.plots.some((plot) => plot.state === "ready") ? 1 : 0;
    case "stage": return inRange(state, "pen") && state.arrivalEligibility.penTick !== null && state.tick >= state.arrivalEligibility.penTick && cargoKind(state) === "corn" && cargoCount > 0 && stagedCount + cargoCount <= MANUAL_CHAIN_TUNING.feedCapacity ? cargoCount : 0;
    case "tend": return inRange(state, "pen") && cargoCount === 0 && stagedCount > 0 && eggCount + Math.min(stagedCount, MANUAL_CHAIN_TUNING.initialChickenCount) <= MANUAL_CHAIN_TUNING.eggCapacity ? Math.min(stagedCount, MANUAL_CHAIN_TUNING.initialChickenCount) : 0;
    case "collect": return inRange(state, "pen") && (cargoKind(state) === "none" || cargoKind(state) === "egg") && eggCount > 0 && cargoCount < state.player.cargoCapacity ? 1 : 0;
    case "sale": return inRange(state, "market") && state.arrivalEligibility.marketTick !== null && state.tick >= state.arrivalEligibility.marketTick && cargoKind(state) === "egg" && cargoCount > 0 ? cargoCount : 0;
  }
}

function requiredTicks(verb: ManualChainInteraction, quantity: number): number {
  if (verb === "tend") return MANUAL_CHAIN_TUNING.tendTicks;
  if (verb === "stage" || verb === "sale") {
    return MANUAL_CHAIN_TUNING.batchContactTicks + MANUAL_CHAIN_TUNING.batchPerUnitTicks * quantity;
  }
  return MANUAL_CHAIN_TUNING.unitActionTicks;
}

function cancelActiveAction(state: Readonly<ManualChainState>): ManualChainState {
  const active = state.activeAction;
  if (active === null) return state as ManualChainState;
  const cancelled = appendManualChainEvent(state, {
    kind: "action-cancelled",
    actorId: 1,
    actionId: active.actionId,
    workOrderId: active.workOrderId,
    quantity: active.quantity,
  });
  return { ...cancelled, activeAction: null };
}

export interface ManualChainSimulation {
  readonly initialSnapshot: ManualChainSnapshot;
  readonly tick: (snapshot: ManualChainSnapshot, input: Readonly<ManualChainTickInput>) => ManualChainSnapshot;
}

export function createManualChainSimulation(s04: ManualChainS04Port): ManualChainSimulation {
  let currentInput: Readonly<ManualChainTickInput> = Object.freeze({
    tick: 1,
    tickMs: 50,
    heldInput: EMPTY_HELD_INPUT,
    command: Object.freeze({ interaction: null, routeTarget: null, explicitCancel: false, actorPresent: true, bodyVisible: true }),
  });

  const phase1: PhaseHandler<ManualChainState> = (root) => {
    const command = currentInput.command;
    let state: ManualChainState = {
      ...root,
      tick: currentInput.tick,
      player: {
        ...root.player,
        present: command.actorPresent,
        bodyVisible: command.bodyVisible,
        moving: false,
      },
      ledger: replaceActorAction(root.ledger, root.ledger.actors[0]?.actionId ?? 0, command.actorPresent),
    };
    const invalidActive = state.activeAction !== null && (
      command.explicitCancel ||
      !command.actorPresent ||
      !command.bodyVisible ||
      command.interaction !== state.activeAction.verb
    );
    if (invalidActive) state = cancelActiveAction(state);
    return state;
  };

  const movement: PhaseHandler<ManualChainState> = (root) => {
    if (!root.player.present || !root.player.bodyVisible || root.activeAction !== null) return root;
    const routeTarget = currentInput.command.routeTarget;
    let route = root.route;
    if (route === null && routeTarget !== null) {
      const point = MANUAL_CHAIN_POINTS[routeTarget];
      if (root.player.xMicroWU !== point.xMicroWU || root.player.zMicroWU !== point.zMicroWU) {
        route = {
          target: routeTarget,
          startTick: currentInput.tick,
          durationTicks: MANUAL_CHAIN_TUNING.routeTicks,
          startXMicroWU: root.player.xMicroWU,
          startZMicroWU: root.player.zMicroWU,
          targetXMicroWU: point.xMicroWU,
          targetZMicroWU: point.zMicroWU,
        };
      }
    }
    if (route !== null) {
      if (routeTarget !== route.target) return { ...root, route: null };
      const progress = Math.min(route.durationTicks, currentInput.tick - route.startTick + 1);
      const x = route.startXMicroWU + Math.trunc((route.targetXMicroWU - route.startXMicroWU) * progress / route.durationTicks);
      const z = route.startZMicroWU + Math.trunc((route.targetZMicroWU - route.startZMicroWU) * progress / route.durationTicks);
      const complete = progress === route.durationTicks;
      const resolved = resolveFirstYardMovement(
        { xMicroWU: root.player.xMicroWU, zMicroWU: root.player.zMicroWU },
        x - root.player.xMicroWU,
        z - root.player.zMicroWU,
      );
      const reachedTarget = resolved.xMicroWU === route.targetXMicroWU && resolved.zMicroWU === route.targetZMicroWU;
      return {
        ...root,
        route: complete && reachedTarget ? null : route,
        player: {
          ...root.player,
          xMicroWU: resolved.xMicroWU,
          zMicroWU: resolved.zMicroWU,
          facingXMicroWU: Math.sign(route.targetXMicroWU - route.startXMicroWU) * 1_000_000,
          facingZMicroWU: Math.sign(route.targetZMicroWU - route.startZMicroWU) * 1_000_000,
          moving: resolved.xMicroWU !== root.player.xMicroWU || resolved.zMicroWU !== root.player.zMicroWU,
        },
      };
    }
    const held = currentInput.heldInput;
    if (held.movementX === 0 && held.movementZ === 0) return root;
    const diagonal = held.movementX !== 0 && held.movementZ !== 0;
    const step = diagonal ? MANUAL_CHAIN_TUNING.diagonalStepMicroWU : MANUAL_CHAIN_TUNING.directStepMicroWU;
    const resolved = resolveFirstYardMovement(
      { xMicroWU: root.player.xMicroWU, zMicroWU: root.player.zMicroWU },
      held.movementX * step,
      held.movementZ * step,
    );
    return {
      ...root,
      player: {
        ...root.player,
        xMicroWU: resolved.xMicroWU,
        zMicroWU: resolved.zMicroWU,
        facingXMicroWU: held.movementX * 1_000_000,
        facingZMicroWU: held.movementZ * 1_000_000,
        moving: resolved.xMicroWU !== root.player.xMicroWU || resolved.zMicroWU !== root.player.zMicroWU,
      },
    };
  };

  const actions: PhaseHandler<ManualChainState> = (root) => {
    const interaction = currentInput.command.interaction;
    if (currentInput.command.explicitCancel) return root;
    if (!root.player.present || !root.player.bodyVisible || root.route !== null || interaction === null) return root;
    if (root.activeAction === null) {
      const quantity = eligibleQuantity(root, interaction);
      if (quantity === 0) return root;
      const actionId = root.counters.nextActionId;
      const action: ManualChainActiveAction = {
        verb: interaction,
        actionId,
        workOrderId: 1,
        startTick: currentInput.tick,
        progressTicks: 1,
        requiredTicks: requiredTicks(interaction, quantity),
        quantity,
        nextUnitTick: interaction === "harvest" || interaction === "collect" ? currentInput.tick + MANUAL_CHAIN_TUNING.unitActionTicks - 1 : null,
      };
      let state: ManualChainState = {
        ...root,
        activeAction: action,
        ledger: replaceActorAction(root.ledger, actionId, true),
        counters: { ...root.counters, nextActionId: actionId + 1 },
      };
      if (interaction === "tend") state = appendManualChainEvent(state, {
        kind: "tend-started", actorId: 1, actionId, workOrderId: 1, quantity,
      });
      return state;
    }
    return {
      ...root,
      activeAction: { ...root.activeAction, progressTicks: root.activeAction.progressTicks + 1 },
    };
  };

  function s04Accepted(state: Readonly<ManualChainState>, verb: "stage" | "tend" | "collect", itemIds: readonly number[]) {
    return s04.commit(Object.freeze({
      verb,
      tick: state.tick,
      actorId: 1,
      bodyId: 11,
      actionId: state.activeAction!.actionId,
      workOrderId: state.activeAction!.workOrderId,
      itemIds: Object.freeze([...itemIds]),
      ledger: state.ledger,
      lifecycleSlice: state.lifecycleSlice,
    }));
  }

  const transfers: PhaseHandler<ManualChainState> = (root) => {
    const action = root.activeAction;
    if (action === null || action.progressTicks < action.requiredTicks || action.verb === "tend") return root;
    let state: ManualChainState = root as ManualChainState;
    const idempotencyKey = state.counters.nextIdempotencyKey;
    if (action.verb === "harvest") {
      const plot = state.plots.find((candidate) => candidate.state === "ready");
      if (plot === undefined) return cancelActiveAction(state);
      const itemId = state.counters.nextItemId;
      const result = commitCreate(state.ledger, {
        actorId: 1,
        actionId: action.actionId,
        itemId,
        kind: 1,
        lineageId: itemId,
        destination: ref(state.ledger, OWNER_KIND.ACTOR_CARGO, OWNER_ID.cargo),
        capacityClaim: 1,
        navRevision: null,
        reservationLeaseId: null,
        idempotencyKey,
        cancelled: false,
      }, null, state.tick);
      if (!result.committed) return cancelActiveAction(state);
      state = {
        ...state,
        ledger: result.state,
        plots: state.plots.map((candidate) => candidate.id === plot.id ? { ...candidate, state: "cooldown", cooldownTicks: MANUAL_CHAIN_TUNING.plotCooldownTicks } : candidate),
        activeAction: null,
        counters: { ...state.counters, nextItemId: itemId + 1, nextIdempotencyKey: idempotencyKey + 1 },
      };
      if (countOwnedItems(state, OWNER_KIND.ACTOR_CARGO, 1, 1) === 4 && state.arrivalEligibility.penTick === null) {
        state = { ...state, arrivalEligibility: { ...state.arrivalEligibility, penTick: state.tick + MANUAL_CHAIN_TUNING.routeTicks + 1 } };
      }
      return appendManualChainEvent(state, {
        kind: "harvest-committed", actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId,
        itemId, plotId: plot.id, lineageId: itemId, quantity: 1,
      });
    }
    if (action.verb === "stage") {
      const itemIds = state.ledger.items.filter((item) => item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1 && item.kind === 1).map((item) => item.itemId);
      const gate = s04Accepted(state, "stage", itemIds);
      if (!gate.accepted) return cancelActiveAction({ ...state, lifecycleSlice: gate.lifecycleSlice });
      const result = commitTransfer(state.ledger, {
        actorId: 1, actionId: action.actionId, itemIds,
        source: ref(state.ledger, OWNER_KIND.ACTOR_CARGO, OWNER_ID.cargo),
        destination: ref(state.ledger, OWNER_KIND.PEN_STAGING, OWNER_ID.pen),
        expectedItemKind: 1, capacityClaim: itemIds.length, navRevision: null,
        reservationLeaseId: null, idempotencyKey, cancelled: false,
      }, null, state.tick);
      if (!result.committed) return cancelActiveAction(state);
      state = { ...state, ledger: result.state, lifecycleSlice: gate.lifecycleSlice, activeAction: null, counters: { ...state.counters, nextIdempotencyKey: idempotencyKey + 1 } };
      return appendManualChainEvent(state, { kind: "stage-committed", actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId, quantity: itemIds.length });
    }
    if (action.verb === "collect") {
      const item = state.ledger.items.find((candidate) => candidate.ownerKind === OWNER_KIND.PEN_OUTPUT && candidate.ownerId === 2 && candidate.kind === 2);
      if (item === undefined) return cancelActiveAction(state);
      const gate = s04Accepted(state, "collect", [item.itemId]);
      if (!gate.accepted) return cancelActiveAction({ ...state, lifecycleSlice: gate.lifecycleSlice });
      const result = commitTransfer(state.ledger, {
        actorId: 1, actionId: action.actionId, itemIds: [item.itemId],
        source: ref(state.ledger, OWNER_KIND.PEN_OUTPUT, OWNER_ID.pen),
        destination: ref(state.ledger, OWNER_KIND.ACTOR_CARGO, OWNER_ID.cargo),
        expectedItemKind: 2, capacityClaim: 1, navRevision: null,
        reservationLeaseId: null, idempotencyKey, cancelled: false,
      }, null, state.tick);
      if (!result.committed) return cancelActiveAction(state);
      state = { ...state, ledger: result.state, lifecycleSlice: gate.lifecycleSlice, activeAction: null, counters: { ...state.counters, nextIdempotencyKey: idempotencyKey + 1 } };
      if (countOwnedItems(state, OWNER_KIND.ACTOR_CARGO, 1, 2) === 4 && state.arrivalEligibility.marketTick === null) {
        state = { ...state, arrivalEligibility: { ...state.arrivalEligibility, marketTick: state.tick + MANUAL_CHAIN_TUNING.routeTicks + 1 } };
      }
      return appendManualChainEvent(state, { kind: "collect-committed", actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId, itemId: item.itemId, lineageId: item.lineageId, quantity: 1 });
    }
    const eggIds = state.ledger.items.filter((item) => item.ownerKind === OWNER_KIND.ACTOR_CARGO && item.ownerId === 1 && item.kind === 2).map((item) => item.itemId);
    const result = commitEggSaleBatch(state.ledger, {
      actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId,
      cargoOwner: ref(state.ledger, OWNER_KIND.ACTOR_CARGO, OWNER_ID.cargo),
      marketOwner: ref(state.ledger, OWNER_KIND.MARKET_SERVICE, OWNER_ID.market),
      eggItemIds: eggIds, cashPerEgg: MANUAL_CHAIN_TUNING.cashPerEgg,
      idempotencyKey, cancelled: false,
    });
    if (!result.committed) return cancelActiveAction(state);
    state = { ...state, ledger: result.state, activeAction: null, counters: { ...state.counters, nextIdempotencyKey: idempotencyKey + 1 } };
    return appendManualChainEvent(state, { kind: "sale-committed", actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId, quantity: eggIds.length });
  };

  const reactions: PhaseHandler<ManualChainState> = (root) => {
    const action = root.activeAction;
    if (action === null || action.verb !== "tend" || action.progressTicks < action.requiredTicks) return root;
    const cornItems = root.ledger.items.filter((item) => item.ownerKind === OWNER_KIND.PEN_STAGING && item.ownerId === 2 && item.kind === 1).slice(0, action.quantity);
    const gate = s04Accepted(root, "tend", cornItems.map((item) => item.itemId));
    if (!gate.accepted) return cancelActiveAction({ ...root, lifecycleSlice: gate.lifecycleSlice });
    const eggItemIds = cornItems.map((_, index) => root.counters.nextItemId + index);
    const idempotencyKey = root.counters.nextIdempotencyKey;
    const result = commitAttendedTendBatch(root.ledger, {
      actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId,
      stagedOwner: ref(root.ledger, OWNER_KIND.PEN_STAGING, OWNER_ID.pen),
      outputOwner: ref(root.ledger, OWNER_KIND.PEN_OUTPUT, OWNER_ID.pen),
      cornItemIds: cornItems.map((item) => item.itemId), eggItemIds,
      idempotencyKey, cancelled: false,
    });
    if (!result.committed) return cancelActiveAction(root);
    let state: ManualChainState = {
      ...root,
      ledger: result.state,
      lifecycleSlice: gate.lifecycleSlice,
      activeAction: null,
      counters: {
        ...root.counters,
        nextItemId: root.counters.nextItemId + eggItemIds.length,
        nextIdempotencyKey: idempotencyKey + 1,
      },
    };
    state = appendManualChainEvent(state, { kind: "tend-committed", actorId: 1, actionId: action.actionId, workOrderId: action.workOrderId, quantity: eggItemIds.length });
    return state;
  };

  const finalize: PhaseHandler<ManualChainState> = (root) => {
    const harvestedThisTick = new Set(root.events.filter((event) => event.tick === root.tick && event.kind === "harvest-committed").map((event) => event.plotId));
    let state: ManualChainState = {
      ...root,
      plots: root.plots.map((plot) => {
        if (plot.state !== "cooldown" || harvestedThisTick.has(plot.id)) return plot;
        const cooldownTicks = Math.max(0, plot.cooldownTicks - 1);
        return { ...plot, cooldownTicks, state: cooldownTicks === 0 ? "ready" : "cooldown" };
      }),
    };
    const objective = deriveObjective(state);
    if (objective !== state.objectiveId) {
      state = { ...state, objectiveId: objective };
      state = appendManualChainEvent(state, { kind: "objective-changed", actorId: 1, actionId: state.ledger.actors[0]?.actionId ?? 0, workOrderId: 1 });
    }
    if (!state.penExtensionAvailable && state.ledger.currency >= MANUAL_CHAIN_TUNING.penExtensionPrice) {
      state = { ...state, penExtensionAvailable: true };
      state = appendManualChainEvent(state, { kind: "pen-extension-ready", actorId: 1, actionId: state.ledger.actors[0]?.actionId ?? 0, workOrderId: 1 });
    }
    return state;
  };

  const kernel = new SimulationKernel<ManualChainState>({
    initialState: createInitialManualChainState() as ManualChainState,
    hashGameplayState: hashManualChainState,
    getLaborEnabledCount: (state) => state.player.present && state.player.bodyVisible ? 1 : 0,
    phaseHandlers: {
      "apply-lifecycle-and-actor-removals": phase1,
      "advance-navigation": movement,
      "arbitrate-triggers": actions,
      "commit-transfers": transfers,
      "complete-node-reactions": reactions,
      "finalize-tender-events-and-checkpoints": finalize,
    },
  });

  return Object.freeze({
    initialSnapshot: kernel.initialSnapshot,
    tick: (snapshot: ManualChainSnapshot, input: Readonly<ManualChainTickInput>): ManualChainSnapshot => {
      currentInput = input;
      return kernel.tick(snapshot, { tick: input.tick, tickMs: input.tickMs, heldInput: input.heldInput });
    },
  });
}

export function stepManualChain(
  simulation: ManualChainSimulation,
  snapshot: ManualChainSnapshot,
  input: Readonly<ManualChainTickInput>,
): ManualChainSnapshot {
  return simulation.tick(snapshot, input);
}
